import React, { useCallback, useEffect, useRef } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useAdminSupportWebSocket, AdminSupportWSEvent } from '../../hooks/useAdminSupportWebSocket';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { ticketService, SupportNotification } from '../../services/ticketService';
import { receivesGlobalSupportAlerts } from '../../utils/systemRoles';
import { useToast } from '../Toast';

import {
  SUPPORT_ADMIN_ALERT_EVENT,
  SUPPORT_NOTIFICATION_REFRESH_EVENT,
} from './supportNotificationEvents';

const SUPPORT_NOTIFICATION_POLL_MS = 30000;
const SUPPORT_ALERT_TOAST_MS = 5000;
const LAST_ACTIVE_STORAGE_KEY = 'support_notification_last_active_at';

const getToastMessage = (event: AdminSupportWSEvent): string => {
  if (event.type === 'support_ticket_created') {
    return 'Customer has created a ticket';
  }

  return 'Customer replied';
};

const getNotificationToastMessage = (notification: SupportNotification): string => {
  if (notification.type === 'support_ticket_staff_message') {
    return 'Support replied';
  }

  if (notification.type === 'support_ticket_created') {
    return 'Customer has created a ticket';
  }

  if (notification.type === 'support_ticket_user_message') {
    return 'Customer replied';
  }

  return notification.title;
};

const getNotificationKey = (notification: SupportNotification): string => {
  const ticketId = notification.data?.ticket_id || notification.url || '';
  const messageId = notification.data?.message_id || notification.created_at || notification.id;

  return `${notification.type}:${ticketId}:${messageId}`;
};

const getStaffEventKey = (event: AdminSupportWSEvent): string => {
  const ticketId = event.data?.ticket_id || '';
  const messageId = event.data?.message_id || event.data?.message_created_at || event.data?.updated_at || '';

  return `${event.type}:${ticketId}:${messageId}`;
};

const getLastActiveAt = (): number => {
  const stored = window.localStorage.getItem(LAST_ACTIVE_STORAGE_KEY);
  const parsed = stored ? Number(stored) : 0;

  return Number.isFinite(parsed) ? parsed : 0;
};

const storeLastActiveAt = () => {
  window.localStorage.setItem(LAST_ACTIVE_STORAGE_KEY, String(Date.now()));
};

const isNotificationAfter = (notification: SupportNotification, timestamp: number): boolean =>
  timestamp > 0 && new Date(notification.created_at).getTime() > timestamp;

const shouldDeferAlert = (): boolean =>
  document.hidden || !document.hasFocus();

export const SupportNotificationBridge: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const toast = useToast();
  const showToast = toast.showToast;

  usePushNotifications();
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());
  const toastedNotificationKeysRef = useRef<Set<string>>(new Set());
  const pendingNotificationsRef = useRef<Map<string, SupportNotification>>(new Map());

  const isSupportStaff =
    isAuthenticated &&
    receivesGlobalSupportAlerts(user?.system_role);

  useEffect(() => {
    if (!isAuthenticated) {
      seenNotificationIdsRef.current.clear();
      toastedNotificationKeysRef.current.clear();
      pendingNotificationsRef.current.clear();

      return undefined;
    }

    let alive = true;
    let seeded = false;

    const showNotifications = (notifications: SupportNotification[]) => {
      notifications.slice(0, 3).forEach((notification) => {
        toastedNotificationKeysRef.current.add(getNotificationKey(notification));
        showToast(getNotificationToastMessage(notification), 'info', [
          {
            label: 'Open ticket',
            href: notification.url || '/support',
          },
        ], SUPPORT_ALERT_TOAST_MS);
      });
    };

    const queueOrShowNotifications = (notifications: SupportNotification[]) => {
      if (notifications.length === 0) return;

      window.dispatchEvent(new Event(SUPPORT_NOTIFICATION_REFRESH_EVENT));

      if (shouldDeferAlert()) {
        notifications.forEach((notification) => {
          pendingNotificationsRef.current.set(getNotificationKey(notification), notification);
        });

        return;
      }

      showNotifications(notifications);
    };

    const flushPendingNotifications = () => {
      const pending = Array.from(pendingNotificationsRef.current.values()).filter(
        (notification) => !toastedNotificationKeysRef.current.has(getNotificationKey(notification))
      );

      pendingNotificationsRef.current.clear();
      showNotifications(pending);
    };

    const loadUnreadNotifications = async (alertExisting = false) => {
      try {
        const data = await ticketService.listNotifications({
          page: 1,
          per_page: 10,
          unread_only: true,
        });

        if (!alive) return;

        const currentIds = new Set(data.notifications.map((item) => item.id));
        const currentKeys = new Set(data.notifications.map(getNotificationKey));
        const lastActiveAt = getLastActiveAt();
        const shouldAlertExisting = (notification: SupportNotification) => (
          alertExisting &&
          isNotificationAfter(notification, lastActiveAt) &&
          !toastedNotificationKeysRef.current.has(getNotificationKey(notification))
        );

        if (!seeded) {
          const existingAlerts = data.notifications.filter(shouldAlertExisting);
          const existingAlertKeys = new Set(existingAlerts.map(getNotificationKey));
          seenNotificationIdsRef.current = currentIds;
          toastedNotificationKeysRef.current = new Set([
            ...Array.from(toastedNotificationKeysRef.current),
            ...Array.from(currentKeys).filter((key) => !existingAlertKeys.has(key)),
          ]);
          seeded = true;
          window.dispatchEvent(new Event(SUPPORT_NOTIFICATION_REFRESH_EVENT));
          queueOrShowNotifications(existingAlerts);

          return;
        }

        const nextNotifications = data.notifications.filter(
          (item) => (
            (!seenNotificationIdsRef.current.has(item.id) || shouldAlertExisting(item)) &&
            !toastedNotificationKeysRef.current.has(getNotificationKey(item))
          )
        );

        seenNotificationIdsRef.current = new Set([
          ...Array.from(seenNotificationIdsRef.current),
          ...Array.from(currentIds),
        ]);

        if (nextNotifications.length === 0) return;

        queueOrShowNotifications(nextNotifications);
      } catch {
        // DB notifications are best-effort for live toasts; the bell keeps polling too.
      }
    };

    loadUnreadNotifications(true);
    const interval = window.setInterval(() => {
      if (shouldDeferAlert()) return;
      loadUnreadNotifications();
    }, SUPPORT_NOTIFICATION_POLL_MS);
    const handleActive = () => {
      if (document.hidden) return;
      flushPendingNotifications();
      loadUnreadNotifications(true);
    };
    const handleInactive = () => {
      if (document.hidden) storeLastActiveAt();
    };

    window.addEventListener('focus', handleActive);
    window.addEventListener('blur', storeLastActiveAt);
    window.addEventListener('beforeunload', storeLastActiveAt);
    document.addEventListener('visibilitychange', handleInactive);
    document.addEventListener('visibilitychange', handleActive);

    return () => {
      alive = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', handleActive);
      window.removeEventListener('blur', storeLastActiveAt);
      window.removeEventListener('beforeunload', storeLastActiveAt);
      document.removeEventListener('visibilitychange', handleInactive);
      document.removeEventListener('visibilitychange', handleActive);
    };
  }, [isAuthenticated, showToast]);

  const handleEvent = useCallback(
    (event: AdminSupportWSEvent) => {
      if (!event.data?.ticket_id) return;

      window.dispatchEvent(
        new CustomEvent(SUPPORT_ADMIN_ALERT_EVENT, { detail: event })
      );
      window.dispatchEvent(new Event(SUPPORT_NOTIFICATION_REFRESH_EVENT));

      if (shouldDeferAlert()) return;

      toastedNotificationKeysRef.current.add(getStaffEventKey(event));
      showToast(getToastMessage(event), 'info', [
        {
          label: 'Open ticket',
          href: `/support/tickets/${event.data.ticket_id}`,
        },
      ], SUPPORT_ALERT_TOAST_MS);
    },
    [showToast]
  );

  useAdminSupportWebSocket(isSupportStaff, handleEvent);

  return null;
};
